// Map cart.GetCart → lean Cart shape consumed by the UI.

import {
  normalizeCartModifier,
  normalizeCartServiceItem,
  normalizeCartVariant,
} from '@core/utils/cartSelections';

function pickImage(raw = {}) {
  const item = raw.item ?? raw.item_details ?? raw.item_detail ?? raw.item_data ?? {};
  const imageList = [
    ...(Array.isArray(raw.images) ? raw.images : []),
    ...(Array.isArray(raw.item_images) ? raw.item_images : []),
    ...(Array.isArray(item.images) ? item.images : []),
    ...(Array.isArray(item.item_images) ? item.item_images : []),
  ];
  const defaultImage = imageList.find((image) => image?.is_default) ?? imageList[0];
  return (
    raw.online_image?.url ??
    item.online_image?.url ??
    defaultImage?.url ??
    defaultImage?.image_url ??
    defaultImage ??
    raw.image_url ??
    raw.item_image_url ??
    item.image_url ??
    item.item_image_url ??
    undefined
  );
}

/**
 * @param {object} raw cart.GetCartLineItem
 * @returns {import('@core/types').CartLine}
 */
export function toCartLine(raw) {
  const item =
    raw.item ?? raw.item_details ?? raw.item_detail ?? raw.item_data ?? raw.itemDetails ?? null;
  const selectedVariants = Array.isArray(raw.selected_variants)
    ? raw.selected_variants.map(normalizeCartVariant)
    : [];
  const selectedModifiers = Array.isArray(raw.selected_modifiers)
    ? raw.selected_modifiers.map(normalizeCartModifier)
    : [];
  const variant = raw.variant
    ? normalizeCartVariant(raw.variant)
    : selectedVariants[0]
      ? selectedVariants[0]
      : undefined;
  const modifiers = Array.isArray(raw.modifiers)
    ? raw.modifiers.map(normalizeCartModifier)
    : selectedModifiers;
  const serviceItems = Array.isArray(raw.service_items)
    ? raw.service_items.map(normalizeCartServiceItem)
    : [];
  const serviceItemGroups = Array.isArray(raw.service_item_groups) ? raw.service_item_groups : [];

  return {
    lineId: raw.line_id,
    itemId: raw.item_id,
    itemVariantSkuId: raw.item_variant_sku_id ?? undefined,
    itemName: raw.item_name ?? '',
    imageUrl: pickImage(raw),
    quantity: raw.quantity ?? 0,
    notes: typeof raw.notes === 'string' ? raw.notes : '',
    basePrice: raw.base_price ?? 0,
    lineSubtotal: raw.line_subtotal ?? 0,
    lineTotal: raw.line_total ?? 0,
    taxes: Array.isArray(raw.taxes) ? raw.taxes : [],
    taxAmount: raw.tax_amount ?? raw.item_tax_amount ?? 0,
    mrp:
      raw.mrp ??
      raw.buying_price ??
      raw.item_mrp ??
      raw.item?.mrp ??
      raw.item?.buying_price ??
      undefined,
    uom: raw.uom ?? undefined,
    alternativeUnit: raw.alternative_unit ?? undefined,
    alternativeUnitDetails: raw.alternative_unit_details ?? undefined,
    item,
    variant,
    selectedVariants,
    selectedModifiers,
    modifiers,
    serviceItems,
    serviceItemGroups,
  };
}

/**
 * @param {object} raw cart.GetCart
 * @returns {import('@core/types').Cart}
 */
export function toCart(raw) {
  if (!raw) return null;
  return {
    cartId: raw.cart_id,
    status: raw.status ?? '',
    items: Array.isArray(raw.items) ? raw.items.map(toCartLine) : [],
    subtotal: raw.subtotal ?? 0,
    taxTotal: raw.tax_total ?? 0,
    discountAmount: raw.discount_amount ?? 0,
    deliveryCharge: raw.delivery_charge ?? 0,
    chargesBreakdown: Array.isArray(raw.charges_breakdown) ? raw.charges_breakdown : [],
    grandTotal: raw.grand_total ?? 0,
    currencyConfig: raw.currency_config ?? undefined,
  };
}
